/**
 * Dependencies.
 */
import { RawLocation, Route, RouteConfig } from 'vue-router';

import { partnershipService } from '@/api/detail/partnership/partnership.service';
import {
  CampaignTypeEnum,
  ON_DEMAND_CAMPAIGN_TYPES
} from '@/api/on-demand-campaigns/campaign-for-partnership.model';
import { campaignForPartnershipService } from '@/api/on-demand-campaigns/campaign-for-partnership.service';
import { ROUTES } from '@/constants/routes.constant';

/**
 * Detail route name by campaign type (types handled inside this app).
 */
const DETAIL_ROUTE_BY_TYPE: Partial<Record<CampaignTypeEnum, string>> = {
  [CampaignTypeEnum.GENERIC]: ROUTES.CAMPAIGNS.GENERICS_DETAIL,
  [CampaignTypeEnum.OFFER]: ROUTES.CAMPAIGNS.CAMPAIGNS_DETAIL
};

/**
 * Resolve the current partnership id: query string, then the first
 * partnership of the logged account.
 */
const resolvePartnershipId = async (to: Route): Promise<number | null> => {
  let partnershipId = Number(to.query.partnershipId) || null;

  if (!partnershipId) {
    const { data: partnerships = [] } =
      (await partnershipService.getPartnerships({ limit: 1, page: 0 })) || {};

    partnershipId = Number(partnerships?.[0]?.id) || null;
  }

  return partnershipId;
};

/**
 * Build a `?key=value` search string from a route query (empty when no params).
 */
const toSearch = (query: Route['query']): string => {
  const search = new URLSearchParams(
    query as Record<string, string>
  ).toString();

  return search ? `?${search}` : '';
};

/**
 * Export.
 */
export default [
  {

    /**
     * Resolve the campaign type and forward to the matching detail scene.
     */
    async beforeEnter(to, _from, next) {
      const { id } = to.params;

      let target: RawLocation | false = false;
      let href: string | null = null;

      try {
        const partnershipId = await resolvePartnershipId(to);

        if (!partnershipId) {
          throw new Error('Partnership not found.');
        }

        const query = { ...to.query, partnershipId: String(partnershipId) };

        const type = await campaignForPartnershipService.getCampaignType({
          id: Number(id),
          partnershipId
        });

        const name = type && DETAIL_ROUTE_BY_TYPE[type];

        if (name) {
          target = { name, params: { id }, query, replace: true };
        } else if (type && ON_DEMAND_CAMPAIGN_TYPES.includes(type)) {
          href = `/apps/on-demand-campaigns/detail/${id}${toSearch(query)}`;
        }
      } catch {
        // Handled by the fallback below.
      }

      if (!target && !href) {
        href = `/${toSearch(to.query)}`;
      }

      if (href) {
        window.location.replace(href);
      } else {
        next(target);
      }
    },
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.CAMPAIGNS_RESOLVE,
    path: 'campaigns/:id(\\d+)/resolve'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/campaigns-list/campaigns-list.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.CAMPAIGNS_LIST,
    path: 'campaigns/offers'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/campaigns-detail/campaigns-detail.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.CAMPAIGNS_DETAIL,
    path: 'campaigns/offers/:id'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/generics-list/generics-list.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.GENERICS_LIST,
    path: 'campaigns/generics'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/generics-detail/generics-detail.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.GENERICS_DETAIL,
    path: 'campaigns/generics/:id'
  }
] as RouteConfig[];
